import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Paper,
  Tab,
  Tabs,
  TextField,
  Typography,
  makeStyles,
} from "@material-ui/core";
import CardWrapper from "@components/Card/CardWrapper";
import { useLazyGetDeviceSnapshotWithGattMeta } from "../queries";
import {
  AvatarAttributeRow,
  buildAvatarAttributeRows,
  GattSection,
  parseGattMeta,
} from "./gatt-meta-utils";

const useStyles = makeStyles((theme) => ({
  root: {
    marginTop: theme.spacing(2),
  },
  header: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
  pageTitle: {
    fontSize: "0.95rem",
    fontWeight: 600,
    letterSpacing: "0.02em",
    textTransform: "uppercase",
    color: theme.palette.text.primary,
  },
  pageSubtitle: {
    fontSize: "0.75rem",
    fontWeight: 400,
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.25),
  },
  search: {
    minWidth: 220,
    maxWidth: 280,
  },
  cards: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.5),
    maxHeight: "min(32rem, 55vh)",
    overflowY: "auto",
    paddingRight: theme.spacing(0.5),
    marginTop: theme.spacing(2),
  },
  fieldCard: {
    padding: theme.spacing(1.75, 2),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.grey[50],
  },
  descriptionText: {
    fontSize: "0.8125rem",
    fontWeight: 400,
    lineHeight: 1.5,
    color: theme.palette.text.secondary,
  },
  dataBlock: {
    marginTop: theme.spacing(1.5),
    paddingTop: theme.spacing(1.5),
    borderTop: `1px solid ${theme.palette.divider}`,
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
  },
  dataRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    minHeight: 28,
    padding: theme.spacing(0.75, 1),
    borderRadius: 4,
    backgroundColor: theme.palette.common.white,
    border: `1px solid ${theme.palette.grey[200]}`,
  },
  dataRowLabel: {
    flexShrink: 0,
    width: 48,
    fontSize: "0.625rem",
    fontWeight: 700,
    letterSpacing: "0.07em",
    textTransform: "uppercase",
    color: theme.palette.grey[600],
  },
  dataRowScroll: {
    flex: 1,
    minWidth: 0,
    overflowX: "auto",
    overflowY: "hidden",
    whiteSpace: "nowrap",
    WebkitOverflowScrolling: "touch",
    scrollbarWidth: "thin",
    "&::-webkit-scrollbar": {
      height: 5,
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: theme.palette.grey[400],
      borderRadius: 4,
    },
  },
  keyText: {
    fontFamily: '"Roboto Mono", "Consolas", "Monaco", monospace',
    fontSize: "0.875rem",
    fontWeight: 600,
    color: theme.palette.primary.dark,
    lineHeight: 1.4,
  },
  valueText: {
    fontFamily: '"Roboto Mono", "Consolas", "Monaco", monospace',
    fontSize: "0.875rem",
    fontWeight: 500,
    color: theme.palette.text.primary,
    lineHeight: 1.4,
  },
  empty: {
    padding: theme.spacing(3),
    textAlign: "center",
    color: theme.palette.text.secondary,
  },
  tabs: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    marginBottom: theme.spacing(0.5),
    "& .MuiTab-root": {
      fontSize: "0.75rem",
      fontWeight: 500,
      minHeight: 40,
      textTransform: "none",
    },
  },
}));

const SECTION_ORDER: GattSection[] = ["att", "cmd", "sts", "dta", "dia"];

const SECTION_TAB_LABELS: Record<GattSection, string> = {
  att: "Attributes",
  cmd: "Commands",
  sts: "States",
  dta: "Data",
  dia: "Diagnostics",
};

interface AvatarRawDetailsProps {
  avatar: {
    att?: Array<{ prop?: string | null; value?: string | null; meta?: string | null }> | null;
    cmd?: Array<{ prop?: string | null; value?: string | null; meta?: string | null }> | null;
    sts?: Array<{ prop?: string | null; value?: string | null; meta?: string | null }> | null;
    dta?: Array<{ prop?: string | null; value?: string | null; meta?: string | null }> | null;
    dia?: Array<{ prop?: string | null; value?: string | null; meta?: string | null }> | null;
  };
  oemItemId?: string | null;
}

function DataRow({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  const classes = useStyles();
  return (
    <Box className={classes.dataRow}>
      <Typography component="span" className={classes.dataRowLabel}>
        {label}
      </Typography>
      <Box className={classes.dataRowScroll}>{children}</Box>
    </Box>
  );
}

function AvatarFieldCard({ row }: { row: AvatarAttributeRow }) {
  const classes = useStyles();

  return (
    <Paper variant="outlined" className={classes.fieldCard} elevation={0}>
      <Typography className={classes.descriptionText}>{row.description}</Typography>

      <Box className={classes.dataBlock}>
        <DataRow label="Key">
          <Typography component="span" className={classes.keyText}>
            {row.prop}
          </Typography>
        </DataRow>
        <DataRow label="Value">
          <Typography component="span" className={classes.valueText}>
            {row.value}
          </Typography>
        </DataRow>
      </Box>
    </Paper>
  );
}

const AvatarRawDetails: React.FC<AvatarRawDetailsProps> = ({
  avatar,
  oemItemId,
}) => {
  const classes = useStyles();
  const [activeSection, setActiveSection] = useState(0);
  const [search, setSearch] = useState("");

  const [fetchGattMeta, { data: gattData, loading: gattLoading }] =
    useLazyGetDeviceSnapshotWithGattMeta({
      oemItemId: oemItemId || "",
    });

  useEffect(() => {
    if (!oemItemId?.trim()) return;
    fetchGattMeta();
  }, [oemItemId, fetchGattMeta]);

  const gattMeta = useMemo(
    () => parseGattMeta(gattData?.getDeviceSnapshotWithGattMeta?.gatt_meta),
    [gattData]
  );

  const rows = useMemo(
    () => buildAvatarAttributeRows(avatar, gattMeta),
    [avatar, gattMeta]
  );

  const sectionCounts = useMemo(() => {
    const counts: Record<GattSection, number> = {
      att: 0,
      cmd: 0,
      sts: 0,
      dta: 0,
      dia: 0,
    };
    rows.forEach((row) => {
      counts[row.section] += 1;
    });
    return counts;
  }, [rows]);

  useEffect(() => {
    const firstWithData = SECTION_ORDER.findIndex((s) => sectionCounts[s] > 0);
    if (firstWithData >= 0) setActiveSection(firstWithData);
  }, [sectionCounts]);

  const activeGattSection = SECTION_ORDER[activeSection];

  const filteredRows = useMemo(() => {
    const searchText = search.trim().toLowerCase();
    return rows.filter((row) => {
      if (row.section !== activeGattSection) return false;
      if (!searchText) return true;
      return (
        row.prop.toLowerCase().includes(searchText) ||
        row.value.toLowerCase().includes(searchText) ||
        row.description.toLowerCase().includes(searchText)
      );
    });
  }, [rows, activeGattSection, search]);

  if (rows.length === 0) {
    return null;
  }

  const metaHint = gattLoading
    ? " (loading metadata…)"
    : !oemItemId?.trim()
      ? " — OEM item ID unavailable"
      : "";

  return (
    <Box className={classes.root}>
      <CardWrapper>
        <Box className={classes.header}>
          <Box>
            <Typography className={classes.pageTitle}>
              Raw avatar details
            </Typography>
            <Typography className={classes.pageSubtitle}>
              Field descriptions from GATT metadata{metaHint}
            </Typography>
          </Box>
          <TextField
            className={classes.search}
            size="small"
            variant="outlined"
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Filter fields…"
            InputLabelProps={{ shrink: true }}
          />
        </Box>

        <Tabs
          className={classes.tabs}
          value={activeSection}
          onChange={(_, value: number) => setActiveSection(value)}
          indicatorColor="primary"
          textColor="primary"
          variant="scrollable"
          scrollButtons="auto"
        >
          {SECTION_ORDER.map((section, index) => (
            <Tab
              key={section}
              label={`${SECTION_TAB_LABELS[section]} (${sectionCounts[section]})`}
              disabled={sectionCounts[section] === 0}
              value={index}
            />
          ))}
        </Tabs>

        {filteredRows.length === 0 ? (
          <Typography className={classes.empty} variant="body2">
            {search.trim()
              ? "No fields match your search in this section."
              : "No fields in this section."}
          </Typography>
        ) : (
          <Box className={classes.cards}>
            {filteredRows.map((row) => (
              <AvatarFieldCard key={`${row.section}-${row.prop}`} row={row} />
            ))}
          </Box>
        )}
      </CardWrapper>
    </Box>
  );
};

export default AvatarRawDetails;
